import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiHeader, uiKpis, uiMoney, uiRing, uiTabs, uiTrendChart, uiWireTabs } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { cinemaIpoBell } from "../ui/cinema.js";

/**
 * Investors & Cap Table, redesigned (replaces app.js renderInvestors).
 *
 *  - Who owns the company: a donut of the cap table, each stake's value today.
 *  - Investor sentiment: what moves it (the engine's weights), where it lands
 *    next month, and the investor call.
 *  - Make a move: raise money, sell part of your stake, create an ESOP pool,
 *    buy back stock, pay a dividend. Every amount is a slider; dragging it
 *    redraws the cap table "after this move" with the engine's own math.
 *  - Ownership history from the event log.
 * Data: /api/investors/desk (backend/investor_desk.py). Actions go through
 * /api/actions/execute; selling a stake uses the offer flow
 * (/api/investor-relations/shop-stake, /sell-stake).
 */

// Founder cyan, ESOP green, new money gold; each outside investor its own hue.
const IV_COLORS = { Founder: "#7ee8fa", ESOP: "#5fd39a", New: "#ffc857" };
const IV_INVESTOR_HUES = ["#b39ddb", "#f48fb1", "#ff8a65", "#9fa8da", "#ce93d8", "#80cbc4", "#e6a5ff", "#f6b26b"];
function ivColor(t, i = 0) { return IV_COLORS[t] || IV_INVESTOR_HUES[i % IV_INVESTOR_HUES.length]; }
/** A colour per cap-table row, so no two outside holders look alike. */
function ivPaint(rows) { let k = 0; return rows.map((e) => ({ ...e, color: IV_COLORS[e.type] || ivColor(e.type, k++) })); }
const ivTone = (s) => (s >= 65 ? "var(--sage)" : s >= 40 ? "var(--neon-gold)" : "var(--oxblood)");

function ivDonut(entries, { size = 220, centre = "", sub = "" } = {}) {
  const r = size / 2 - 16, c = size / 2, sw = 26;
  let a0 = -Math.PI / 2;
  const total = entries.reduce((s, e) => s + Math.max(0, e.pct), 0) || 100;
  const arcs = ivPaint(entries).filter((e) => e.pct > 0.01).map((e) => {
    const frac = Math.min(0.9999, e.pct / total), a1 = a0 + frac * 2 * Math.PI;
    const p = (a) => `${(c + r * Math.cos(a)).toFixed(2)} ${(c + r * Math.sin(a)).toFixed(2)}`;
    const d = `M ${p(a0)} A ${r} ${r} 0 ${frac > 0.5 ? 1 : 0} 1 ${p(a1)}`;
    a0 = a1;
    return `<path d="${d}" stroke="${e.color}" stroke-width="${sw}" fill="none"><title>${esc(e.name)}: ${e.pct.toFixed(1)}%</title></path>`;
  }).join("");
  return `<svg class="iv-donut" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="Cap table">
    <circle cx="${c}" cy="${c}" r="${r}" stroke="rgba(255,255,255,0.05)" stroke-width="${sw}" fill="none"/>${arcs}
    <text x="${c}" y="${c - 2}" text-anchor="middle" class="iv-donut-big">${esc(centre)}</text>
    <text x="${c}" y="${c + 18}" text-anchor="middle" class="iv-donut-sub">${esc(sub)}</text></svg>`;
}

function ivSlider(id, label, { min, max, step, value, fmt }) {
  return `<div class="lx-slider">
    <div class="lx-slider-head"><span>${esc(label)}</span><b id="${id}-val">${fmt(value)}</b></div>
    <div class="lx-slider-track"><input type="range" id="${id}" min="${min}" max="${max}" step="${step}" value="${value}" aria-label="${esc(label)}"/></div>
    <div class="lx-slider-scale"><span>${fmt(min)}</span><span>${fmt(max)}</span></div></div>`;
}

/** The cap table after a move, using the engine's own math for each action. */
function ivAfter(d, move, v) {
  const f = d.founder_pct, rows = d.entries.map((e) => ({ ...e }));
  const founder = rows.find((e) => e.type === "Founder");
  const others = rows.filter((e) => e.type !== "Founder");
  let valuation = d.valuation, cash = d.cash, sentiment = 0, note = "";
  if (move === "raise") {
    const R = d.raise, nf = f * (R.effective_pre_money / (R.effective_pre_money + v));
    others.forEach((e) => { e.pct *= nf / f; });
    const fresh = 100 - nf - others.reduce((s, e) => s + e.pct, 0);
    founder.pct = nf; rows.push({ name: "New investors", type: "New", pct: fresh });
    valuation += v; cash += v;
    note = `New investors pay ${uiMoney(v, { compact: true })} for ${fresh.toFixed(1)}% of the company.`;
  } else if (move === "stake") {
    founder.pct = f - v; rows.push({ name: "The buyer", type: "New", pct: v });
    note = `They're your own shares, so the money goes to you (your personal cash), not the company. Buyers pay around ${uiMoney(d.valuation * v / 100, { compact: true })}; the exact price depends on the offer.`;
  } else if (move === "esop") {
    const nf = f - v;
    others.forEach((e) => { e.pct *= nf / f; });
    founder.pct = nf; rows.push({ name: "New ESOP pool", type: "New", pct: 100 - nf - others.reduce((s, e) => s + e.pct, 0) });
    note = "Shares set aside to reward future hires. No money changes hands.";
  } else if (move === "buyback") {
    const back = Math.min(15, (v / Math.max(1, d.valuation)) * 100), tot = d.buyback.others_pct;
    if (tot > 0.01) {
      const k = Math.max(0, 1 - back / tot);
      others.forEach((e) => { e.pct *= k; });
      founder.pct = 100 - others.reduce((s, e) => s + e.pct, 0);
      note = `Buys back about ${Math.min(back, tot).toFixed(1)}% from other holders.`;
    } else note = "Nobody else owns shares yet, so a buyback can't raise your stake; it still signals confidence.";
    cash -= v; sentiment = d.buyback.sentiment_gain;
  } else if (move === "dividend") {
    cash -= v;
    note = `Shareholders get paid in proportion to their stakes. Your share: ${uiMoney(v * f / 100, { compact: true })}.`;
  }
  const nf = founder.pct;
  return { rows, founder: nf, value: valuation * nf / 100, cash, sentiment, note };
}

const IV_MOVES = [
  ["raise", "Raise money"], ["stake", "Sell part of your stake"], ["esop", "ESOP pool"], ["buyback", "Buy back stock"], ["dividend", "Pay a dividend"],
];
const IV_HELP = {
  raise: "New investors put cash into the company in exchange for new shares. Everyone who already owns shares is diluted.",
  stake: "Sell some of your own shares to an outside buyer. Pick how much, then choose between real offers: price, board seats and timing differ.",
  esop: "Set aside shares to reward staff. It makes hiring and keeping people easier, and dilutes you.",
  buyback: "The company buys shares back from other holders. Your stake rises and investors like it, but it spends cash.",
  dividend: "Pay profits out to shareholders. Only possible from retained earnings you actually have.",
};

function ivMoveSpec(d, move) {
  const pct = (x) => `${(+x).toFixed(1)}%`;
  if (move === "raise") return { min: d.raise.min, max: d.raise.max, step: 50_000, value: Math.min(d.raise.max, Math.round(d.raise.max / 6 / 50_000) * 50_000 || d.raise.min), fmt: (x) => uiMoney(+x, { compact: true }), label: "How much to raise" };
  if (move === "stake") return d.stake.ready && d.stake.max >= 0.5 ? { min: 0.5, max: d.stake.max, step: 0.5, value: Math.min(10, d.stake.max), fmt: pct, label: "How much of the company to sell" } : null;
  if (move === "esop") return d.esop.max >= 0.5 ? { min: 0.5, max: d.esop.max, step: 0.5, value: Math.min(5, d.esop.max), fmt: pct, label: "Size of the pool" } : null;
  if (move === "buyback") return d.buyback.max >= 10_000 ? { min: 10_000, max: Math.round(d.buyback.max), step: 10_000, value: Math.round(d.buyback.max / 4 / 10_000) * 10_000 || 10_000, fmt: (x) => uiMoney(+x, { compact: true }), label: "How much to spend" } : null;
  if (move === "dividend") return d.dividend.max >= 10_000 ? { min: 10_000, max: Math.round(d.dividend.max), step: 10_000, value: Math.round(d.dividend.max / 4 / 10_000) * 10_000 || 10_000, fmt: (x) => uiMoney(+x, { compact: true }), label: "How much to pay out" } : null;
  return null;
}

const IV_WHY_NOT = {
  stake: "You need a valuation on record (finish a month) and at least 0.5% of the company.",
  esop: "You don't own enough of the company to carve out a pool.",
  buyback: "Not enough cash, or no valuation on record yet.",
  dividend: "No retained earnings to pay out yet. Dividends come from accumulated profit.",
};

function ivPreview(d, move, v) {
  const a = ivAfter(d, move, v), df = a.founder - d.founder_pct;
  return `
    <div class="iv-after">
      ${ivDonut(a.rows, { size: 170, centre: `${a.founder.toFixed(1)}%`, sub: "you, after" })}
      <div class="iv-deltas">
        <div><span>You own</span><b>${d.founder_pct.toFixed(1)}% → ${a.founder.toFixed(1)}%</b><em class="${df >= 0 ? "pos" : "neg"}">${df >= 0 ? "+" : ""}${df.toFixed(2)} pts</em></div>
        <div><span>Your stake is worth</span><b>${uiMoney(d.founder_value, { compact: true })} → ${uiMoney(a.value, { compact: true })}</b></div>
        <div><span>Company cash</span><b>${uiMoney(d.cash, { compact: true })} → ${uiMoney(a.cash, { compact: true })}</b></div>
        ${a.sentiment ? `<div><span>Investor sentiment</span><b class="pos">+${a.sentiment}</b></div>` : ""}
        <p class="ma-note">${esc(a.note)}</p>
      </div>
    </div>`;
}

function ivMovePanel(d, move) {
  const spec = ivMoveSpec(d, move);
  const btn = { raise: "Raise it", stake: "Get offers", esop: "Create the pool", buyback: "Buy back", dividend: "Pay the dividend" }[move];
  return `
    <p class="iv-help">${esc(IV_HELP[move])}</p>
    ${spec ? `
      <div class="iv-move">
        <div class="iv-move-left">
          ${ivSlider("iv-amt", spec.label, spec)}
          ${move === "raise" ? `<p class="ma-note">${esc(d.raise.note)}</p>` : ""}
          <button type="button" class="primary" id="iv-go">${btn}</button>
        </div>
        <div id="iv-preview">${ivPreview(d, move, spec.value)}</div>
      </div>
      <div id="iv-offers"></div>` : uiEmpty("Not available right now", IV_WHY_NOT[move] || "")}`;
}

function ivOffers(el, data) {
  el.innerHTML = `<h4 class="iv-offers-title">Offers for ${data.pct}% of the company</h4><div class="iv-offer-grid">${data.offers.map((o, i) => `
    <div class="iv-offer ${i === 0 ? "best" : ""}">
      ${i === 0 ? `<span class="bd-hit">Highest price</span>` : ""}
      <span class="sx-eyebrow">${esc(o.archetype)}</span><b>${esc(o.buyer_name)}</b>
      <div class="iv-offer-price">${uiMoney(o.price, { compact: true })}</div>
      <em>Values the company at ${uiMoney(o.implied_company_valuation, { compact: true })}</em>
      <p>${esc(o.pitch)}</p>
      <ul><li>${o.board_seat_requested ? "Wants a board seat" : "No board seat"}</li><li>About ${o.close_months} month${o.close_months === 1 ? "" : "s"} to close</li><li>Sentiment +${o.sentiment_boost}</li></ul>
      <button type="button" class="${i === 0 ? "primary" : ""}" data-sell="${esc(o.buyer_name)}">Sell to them</button>
    </div>`).join("")}</div>`;
  el.querySelectorAll("[data-sell]").forEach((b) => b.addEventListener("click", async () => {
    if (!window.confirm(`Sell ${data.pct}% of the company to ${b.dataset.sell}? This is permanent.`)) return;
    try { toast((await postJSON("/investor-relations/sell-stake", { pct: data.pct, buyer_name: b.dataset.sell })).message); }
    catch (e) { toast(e.message); }
    await window.refreshAll();
  }));
}

function ivSentiment(d) {
  const o = d.outlook;
  return `
    <div class="iv-sent-top">${uiRing(d.sentiment, "Investor sentiment", 84)}
      <div><b class="iv-verdict">${esc(d.verdict)}</b>
        ${o ? `<span>Next month: <b class="${o.next_month >= d.sentiment ? "pos" : "neg"}">${o.next_month.toFixed(0)}</b> (moving towards ${o.target.toFixed(0)})</span>` : ""}</div></div>
    ${d.factors.length ? `<div class="bd-factors">${d.factors.map((f) => `
      <div class="bd-factor ${o && f.key === o.weakest ? "worry" : ""}"><span>${esc(f.label)} <em>${Math.round(f.weight * 100)}%</em></span>
        <div><i style="width:${f.score}%; background:${ivTone(f.score)}"></i></div><b>${Math.round(f.score)}</b></div>`).join("")}</div>
      <p class="ma-note">Valuation ${o.momentum_pct >= 0 ? "up" : "down"} ${Math.abs(o.momentum_pct).toFixed(1)}% over three months; net margin ${o.margin_pct.toFixed(1)}%. Weakest: <b>${esc(o.weakest_label)}</b>.</p>` : ""}
    <button type="button" id="iv-call" class="iv-call">Hold an investor call <span>sentiment +${d.call.gain_low}–${d.call.gain_high}</span></button>
    ${d.call.recent ? `<p class="ma-note">You held one recently: this one will land softer.</p>` : ""}`;
}

/** Go public: readiness against the underwriters' two tests, what the IPO raises, your stake after. */
function ivIpoHtml(p) {
  if (p.is_public) {
    return `<div class="iv-ipo public"><div class="iv-ipo-bell">🔔</div><div><b>Public since month ${p.ipo_month}</b>
      <p class="ma-note">Your shares trade every month: see Public Markets for the price, analysts and earnings calls.</p>
      <button type="button" class="sx-btn" data-ipo-markets>Open Public Markets</button></div></div>`;
  }
  const bar = (label, now, need) => {
    const pct = Math.min(100, (100 * now) / need);
    return `<div class="iv-ipo-test${now >= need ? " ok" : ""}"><span>${label}</span><i><b style="width:${pct.toFixed(1)}%"></b></i>
      <em>${uiMoney(now, { compact: true })} / ${uiMoney(need, { compact: true })}${now >= need ? " ✓" : ""}</em></div>`;
  };
  return `<div class="iv-ipo">
    ${bar("Valuation", p.valuation, p.min_valuation)}
    ${bar("Yearly revenue (run-rate)", p.run_rate, p.min_run_rate)}
    <div class="iv-ipo-deal"><div><em>The company raises</em><b>${uiMoney(p.raise, { compact: true })}</b></div>
      <div><em>You receive</em><b id="iv-ipo-you">—</b></div><div><em>You'd own after</em><b id="iv-ipo-after">${p.founder_after.toFixed(1)}%</b></div></div>
    ${p.founder_sell ? `${ivSlider("iv-ipo-sell", "Sell some of your own shares in the IPO (% of your stake)", { min: 0, max: p.founder_sell.max, step: 1, value: p.founder_sell.default, fmt: (x) => `${x}%` })}
      <p class="ma-note" id="iv-ipo-note"></p>` : ""}
    <button type="button" class="primary iv-ipo-go" id="iv-ipo" ${p.ready ? "" : "disabled"}>🔔 Go public (IPO)</button>
    <p class="ma-note">${p.ready ? "Underwriters are ready: ring the bell to list your shares. It can't be undone." :
      !p.has_close ? "Close your first month to get a valuation, then come back." :
      "Underwriters want both bars full, measured at the last month close. Grow sales and keep a steady track record."}</p></div>`;
}

/** Public Markets (a private company): the IPO card, drawn into `host` (views/core/markets.js calls this). */
window.renderIpoInto = async function (host) {
  const d = await uiGet("/investors/desk");
  host.innerHTML = uiCard("Go public (IPO)", ivIpoHtml(d.ipo),
    { sub: "List your shares on the stock market and raise money from the public. Once listed, your share price trades here every month." });
  // a real IPO sells new shares (money for the company) and some of yours (money for you)
  const fsell = d.ipo.founder_sell, sl = host.querySelector("#iv-ipo-sell");
  const ipoShow = () => {
    if (!fsell || !sl) return { you: 0, after: d.ipo.founder_after };
    const x = +sl.value, you = fsell.per_pct * x, after = d.ipo.founder_after - fsell.pct_of_company_per_pct * x;
    host.querySelector("#iv-ipo-sell-val").textContent = `${x}%`;
    host.querySelector("#iv-ipo-you").textContent = uiMoney(you, { compact: true });
    host.querySelector("#iv-ipo-after").textContent = `${after.toFixed(1)}%`;
    host.querySelector("#iv-ipo-note").textContent = (x ? `Your ${x}% goes into your own pocket at the IPO price, after the banks' ${fsell.fee_pct.toFixed(0)}% fee. ` : "You keep all your shares. ")
      + (x > fsell.nervous_over ? "Selling this much makes investors wonder why the founder is cashing out." : "A small sale like this is normal for founders.");
    return { you, after };
  };
  sl?.addEventListener("input", ipoShow);
  ipoShow();
  host.querySelector("#iv-ipo")?.addEventListener("click", async (e) => {
    const { you, after } = ipoShow();
    if (!confirm(`Go public now? The company raises ${uiMoney(d.ipo.raise, { compact: true })}${you ? `, you receive ${uiMoney(you, { compact: true })}` : ""}, and you'd own ${after.toFixed(1)}% after. This can't be undone.`)) return;
    e.currentTarget.disabled = true;
    try {
      const r = await postJSON("/board/ipo", { founder_sell_pct: sl ? +sl.value : 0 });
      if (/^IPO complete/.test(r.message || "")) await cinemaIpoBell({ name: (document.getElementById("db-company-name") || {}).textContent || "", message: r.message });
      else toast(r.message);
    } catch (err) { toast(err.message); }
    await window.refreshAll();
  });
};

/** Your pay as CEO (founder.py pay_view): a slider from $0 to 3x the market rate. */
function ivPayHtml(p) {
  const step = p.max > 100000 ? 1000 : 500;
  const tone = (x) => (x > 2 ? "neg" : x > 1.2 ? "warn" : x < 1 ? "pos" : "");
  return `<div class="iv-pay">
    <div class="eng-facts"><span>Your pay now <b>${uiMoney(p.salary)}</b>/month</span><span>Market rate <b>${uiMoney(p.market)}</b>/month</span>
      <span>That's <b class="${tone(p.ratio)}">${p.ratio.toFixed(2)}x</b> market</span>
      <span>Cost to the company <b class="${p.company_cost > 0 ? "neg" : p.company_cost < 0 ? "pos" : ""}">${p.company_cost > 0 ? "+" : ""}${uiMoney(p.company_cost)}</b>/month vs budget</span>
      <span>Paid to you so far <b>${uiMoney(p.salary_total, { compact: true })}</b></span></div>
    ${p.can_change ? `${ivSlider("iv-pay", "Your monthly salary", { min: 0, max: p.max, step, value: Math.round(p.salary / step) * step, fmt: (x) => uiMoney(+x) })}
      <div id="iv-pay-preview" class="sx-card-sub"></div>
      <div class="em-actions"><button type="button" class="primary" id="iv-pay-go">Set my pay</button>
        <button type="button" id="iv-pay-one">Take a $1 salary, Jobs-style</button></div>`
      : `<p class="sx-card-sub">The pay committee meets again in month ${p.next_change_month}. Pay can change once every 6 months.</p>`}
    <p class="lx-note">${esc(p.rules)}</p></div>`;
}

function ivPayPreview(p, x) {
  const r = p.market ? x / p.market : 1, diff = x - p.market, bits = [];
  bits.push(diff > 0 ? `The company pays ${uiMoney(diff)} a month more than budgeted.` : diff < 0 ? `The company keeps ${uiMoney(-diff)} a month.` : "Exactly the market rate.");
  if (x > p.salary && r > 1) bits.push(`Investor sentiment about −${Math.min(6, (r - 1) * 3 * (p.is_public ? 2 : 1)).toFixed(1)}${p.is_public ? " (public: shareholders vote on pay)" : ""}.`);
  if (x > p.salary && r > 1 && p.layoffs_recent) bits.push("You laid people off recently: morale and brand take a hit too.");
  if (x > p.salary && r > 2 && p.board_votes) bits.push("Over 2x market: your Board votes on it, and may say no.");
  if (x < p.salary && r < 1) bits.push(`Sentiment and morale up to +${Math.min(3, (1 - r) * 3).toFixed(1)}.${x <= 1 ? " A $1 salary makes headlines (brand +1)." : ""}`);
  return bits.join(" ");
}

window.renderInvestors = async function (main, snap) {
  const d = await uiGet("/investors/desk");
  const move = IV_MOVES.some(([k]) => k === window._ivMove) ? window._ivMove : "raise";
  const legend = ivPaint(d.entries).map((e) => `
    <div class="iv-holder"><i style="background:${e.color}"></i><span>${esc(e.name)}<em>${esc(e.type)}${e.month ? ` · month ${e.month}` : ""}</em></span>
      <b>${e.pct.toFixed(1)}%</b><b>${uiMoney(e.value, { compact: true })}</b></div>`).join("");
  const hist = d.history.filter((h) => h.founder_pct != null);
  main.innerHTML = `
    <div class="sx iv">
      ${uiHeader("Investors & Cap Table", "Who owns the company, how investors feel about it, and every move that changes either.",
        `<button type="button" data-open-valuation>How valuation is counted</button>`)}
      ${uiKpis([
        { label: "You own", value: `${d.founder_pct.toFixed(1)}%` },
        { label: "Your stake is worth", value: uiMoney(d.founder_value, { compact: true }), tone: "pos" },
        { label: "Company valuation", value: d.valuation_status === "not_valued" ? "Not valued yet" : uiMoney(d.valuation, { compact: true }) },
        { label: "ESOP pool", value: `${d.esop_pct.toFixed(1)}%` },
      ])}
      <div class="sx-grid-2">
        ${uiCard("Who owns the company", `<div class="iv-own">${ivDonut(d.entries, { centre: `${d.founder_pct.toFixed(1)}%`, sub: "owned by you" })}
          <div class="iv-holders"><div class="iv-holder head"><i></i><span>Holder</span><b>Stake</b><b>Worth today</b></div>${legend}</div></div>`,
          { sub: "Each stake valued at today's estimated valuation" })}
        ${uiCard("Investor sentiment", ivSentiment(d), { sub: "Sets the price of your next raise" })}
      </div>
      ${d.ipo ? `<div class="iv-ipo-link"><span>${d.ipo.is_public ? `🔔 Public since month ${d.ipo.ipo_month}: your share price is in Public Markets.` :
        d.ipo.ready ? "🔔 You're ready to go public. Ring the bell in Public Markets." : "🔔 Going public (IPO) happens in Public Markets, where your shares will trade."}</span>
        <button type="button" class="sx-btn" data-ipo-markets>Open Public Markets</button></div>` : ""}
      ${uiCard("Make a move", `${uiTabs(IV_MOVES, move, "iv-move")}<div id="iv-panel">${ivMovePanel(d, move)}</div>`,
        { sub: "Drag a slider to see the cap table after the move, before you commit" })}
      ${d.ceo_pay ? uiCard("Your pay as CEO", ivPayHtml(d.ceo_pay), { sub: "Decide what you pay yourself, from $0 to 3x a fair salary for a company your size" }) : ""}
      ${uiCard("Ownership over time", `${hist.length >= 1 ? uiTrendChart([100, ...hist.map((h) => h.founder_pct)], { labels: ["Start", ...hist.map((h) => `M${h.month}`)], money: false, height: 150 }) : ""}
        ${d.history.length ? `<div class="iv-timeline">${d.history.slice().reverse().map((h) => `
          <div><span class="mono">M${h.month}</span><b>${esc(h.label)}</b><em>${h.amount ? uiMoney(h.amount, { compact: true }) : ""}${h.pct ? ` · ${(+h.pct).toFixed(1)}%` : ""}${h.who ? ` · ${esc(h.who)}` : ""}${h.founder_pct != null ? ` · you then owned ${(+h.founder_pct).toFixed(1)}%` : ""}</em></div>`).join("")}</div>`
          : uiEmpty("No changes yet", "You still own the company outright.")}`, { sub: "Your share of the company after each move" })}
      <p class="lx-note">${esc(d.rules)}</p>
    </div>`;
  main.querySelectorAll("[data-open-valuation]").forEach((b) => b.addEventListener("click", () => window.openValuationExplainer()));

  uiWireTabs(main, "iv-move", (k) => { window._ivMove = k; window.renderInvestors(main, snap); });
  const tab = main.querySelector('[data-tabs="iv-move"] .sx-tab.active');
  if (tab && tab.parentElement.scrollWidth > tab.parentElement.clientWidth) tab.parentElement.scrollLeft = tab.offsetLeft - 16;
  const slider = main.querySelector("#iv-amt"), spec = ivMoveSpec(d, move);
  slider?.addEventListener("input", () => {
    main.querySelector("#iv-amt-val").textContent = spec.fmt(slider.value);
    main.querySelector("#iv-preview").innerHTML = ivPreview(d, move, +slider.value);
  });
  const exec = async (type, params) => {
    try { toast((await postJSON("/actions/execute", { action: { type, params } })).message); }
    catch (e) { toast(e.message); }
    await window.refreshAll();
  };
  main.querySelector("#iv-go")?.addEventListener("click", async () => {
    const v = +slider.value;
    if (move === "raise") return exec("raise_capital", { amount: v });
    if (move === "esop") return exec("create_esop_pool", { pct: v });
    if (move === "buyback") return exec("stock_buyback", { amount: v });
    if (move === "dividend") return exec("issue_dividend", { amount: v });
    const box = main.querySelector("#iv-offers");
    box.innerHTML = `<p class="ma-note">Shopping the stake to buyers…</p>`;
    try { ivOffers(box, await postJSON("/investor-relations/shop-stake", { pct: v })); }
    catch (e) { box.innerHTML = `<p class="ma-note">${esc(e.message)}</p>`; }
  });
  main.querySelector("#iv-call")?.addEventListener("click", () => exec("investor_call", {}));
  const pay = main.querySelector("#iv-pay"), payOut = main.querySelector("#iv-pay-preview");
  const payShow = () => { main.querySelector("#iv-pay-val").textContent = uiMoney(+pay.value); payOut.textContent = ivPayPreview(d.ceo_pay, +pay.value); };
  if (pay) { pay.addEventListener("input", payShow); payShow(); }
  main.querySelector("#iv-pay-go")?.addEventListener("click", () => exec("founder_set_salary", { amount: +pay.value }));
  main.querySelector("#iv-pay-one")?.addEventListener("click", () => exec("founder_set_salary", { amount: 1 }));
  main.querySelector("[data-ipo-markets]")?.addEventListener("click", () => { const t = document.querySelector('.sidebar .tab[data-tab="publicmarkets"]'); if (t) t.click(); });
};
